import { useId, useState, type KeyboardEvent } from 'react';
import { normaliseTag } from '../../pages/recipes/recipe-form';

/**
 * Free-form tag entry: type, press Enter (or comma) to commit. Tags are
 * normalised the way `recipeTagSchema` does, so nothing is rejected on submit.
 */
export function TagChipInput({
  value,
  onChange,
  suggestions = [],
  max = 20,
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions?: readonly string[];
  max?: number;
}) {
  const id = useId();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  function add(raw: string) {
    const tag = normaliseTag(raw);
    if (!tag) {
      setError('Use letters, digits, spaces, - or _ (40 characters max).');
      return;
    }
    if (value.includes(tag)) {
      setDraft('');
      setError(null);
      return;
    }
    if (value.length >= max) {
      setError(`That is the most tags a recipe can have (${max}).`);
      return;
    }
    onChange([...value, tag]);
    setDraft('');
    setError(null);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (draft.trim()) add(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }

  const unused = suggestions.filter((s) => !value.includes(s)).slice(0, 8);

  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        Tags
      </label>
      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Chosen tags">
          {value.map((tag) => (
            <li key={tag}>
              <button
                type="button"
                onClick={() => onChange(value.filter((t) => t !== tag))}
                aria-label={`Remove tag ${tag}`}
                className="inline-flex min-h-9 items-center gap-1 rounded-full bg-brand-soft px-3 text-sm text-brand"
              >
                {tag}
                <span aria-hidden>×</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex gap-2">
        <input
          id={id}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (draft.trim()) add(draft);
          }}
          placeholder="weeknight, vegetarian…"
          aria-describedby={error ? `${id}-error` : `${id}-hint`}
          className="min-h-11 min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
        />
        <button
          type="button"
          onClick={() => add(draft)}
          disabled={draft.trim() === ''}
          className="min-h-11 rounded-lg border border-line px-3 text-sm font-semibold text-brand disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-sm text-fail">
          {error}
        </p>
      ) : (
        <p id={`${id}-hint`} className="text-xs text-muted">
          Press Enter after each tag.
        </p>
      )}
      {unused.length > 0 ? (
        <ul className="flex flex-wrap gap-2" aria-label="Tags you have used before">
          {unused.map((tag) => (
            <li key={tag}>
              <button
                type="button"
                onClick={() => add(tag)}
                className="min-h-9 rounded-full border border-line px-3 text-sm text-muted hover:bg-brand-soft hover:text-brand"
              >
                + {tag}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
